import { useCallback, useMemo } from "react";
import {
  useListAccessibleOrganizationsQuery,
  useListOrganizationSubtreeQuery,
} from "~/shared/api/organization/organization.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { isAnyOwnOrganizationForWrite } from "~/shared/lib/own-organization";

/**
 * For a write (save, cancel, routing): fails closed. Undefined while the subtree is loading
 * or failed and none of the orgs is the active company itself (see isAnyOwnOrganizationForWrite).
 * For a job, the orgs it can be handed to (custom access included) count as own too.
 */
export function useIsOwnOrganizationForWrite(resource?: "job") {
  const { activeCompanyId } = useAuthSession();
  const { currentData } = useListOrganizationSubtreeQuery(
    { organizationId: BigInt(activeCompanyId ?? 0) },
    { skip: !activeCompanyId },
  );
  // Failed, it adds nothing: the subtree alone still fails closed.
  const { currentData: accessible, isError } = useListAccessibleOrganizationsQuery(
    { resource: "job", action: "can_edit" },
    { skip: resource !== "job" || !activeCompanyId },
  );
  const orgs = useMemo(
    () =>
      currentData && (resource !== "job" || accessible || isError)
        ? [...(currentData.organizations ?? []), ...(accessible?.organizations ?? [])]
        : undefined,
    [currentData, accessible, isError, resource],
  );
  return useCallback(
    (...ids: (bigint | null | undefined)[]) =>
      isAnyOwnOrganizationForWrite(ids, activeCompanyId, orgs),
    [activeCompanyId, orgs],
  );
}

/**
 * The executors whose jobs the user routes: the active company and the organizations it has
 * custom job access to (the backend expands each to its subtree, executed_only included).
 * The company alone stands in until the access list settles, so the arg changes at most once.
 * An admin reaches every provider, so it routes as the active company only.
 */
export function useJobExecutorIds(): bigint[] {
  const { activeCompanyId, isAdmin } = useAuthSession();
  const { currentData: accessible } = useListAccessibleOrganizationsQuery(
    { resource: "job", action: "can_view" },
    { skip: !activeCompanyId || isAdmin },
  );
  // String(): ids are strings at runtime once cached; the key keeps the array stable across renders.
  const key = activeCompanyId
    ? [
        ...new Set([
          String(activeCompanyId),
          ...(accessible?.organizations ?? []).map((org) => String(org.id)),
        ]),
      ]
        .map(BigInt)
        .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
        .join(",")
    : "";
  return useMemo(() => (key ? key.split(",").map(BigInt) : []), [key]);
}
